/**
 * ReconcileScreen
 * Flat chronological list for quick one-tap-per-day reconciliation
 * Per User Story 2: "flat list showing the last 14 days"
 * Per FR-015: "one tap per day (no multi-step flows)"
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import { ReconcileScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { useCompletions } from '../hooks/useCompletions';
import { getLastNDays, formatDateForDisplay } from '../utils/dateUtils';

interface DayItem {
  date: string;
  dateDisplay: string;
  isCompleted: boolean;
  isToday: boolean;
}

export const ReconcileScreen: React.FC<ReconcileScreenProps> = ({ route, navigation }) => {
  const { goalId } = route.params;
  const { findGoalById, currentAccountId } = useGoals();
  const {
    isCompleted,
    toggleCompletion,
    loadCompletionsForDateRange,
  } = useCompletions();

  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(14); // Default to 14 days per spec

  const goal = findGoalById(goalId);
  const dates = getLastNDays(days);

  const handleToggleDay = async (date: string) => {
    if (!currentAccountId) return;
    await toggleCompletion(currentAccountId, goalId, date);
  };

  useEffect(() => {
    if (currentAccountId && goalId) {
      // Load enough history for stats (365 days) but only display selected range
      const allDates = getLastNDays(365);
      loadCompletionsForDateRange(currentAccountId, goalId, allDates).then(() => {
        setIsLoading(false);
      });
    }
  }, [currentAccountId, goalId]);

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
        </View>
      </SafeAreaView>
    );
  }

  if (!goal) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Goal not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  const today = new Date().toISOString().split('T')[0];

  // Create day items in reverse chronological order (most recent first)
  const dayItems: DayItem[] = dates.map(date => ({
    date,
    dateDisplay: formatDateForDisplay(date),
    isCompleted: isCompleted(goalId, date),
    isToday: date === today,
  }));

  const incompleteDays = dayItems.filter(d => !d.isCompleted).length;

  const renderDayRow = ({ item }: { item: DayItem }) => (
    <TouchableOpacity
      style={[
        styles.dayRow,
        item.isCompleted && styles.dayRowCompleted,
        item.isToday && styles.dayRowToday,
      ]}
      onPress={() => handleToggleDay(item.date)}
      accessibilityLabel={`${item.dateDisplay}, ${item.isCompleted ? 'completed' : 'incomplete'}`}
      accessibilityHint="Tap to toggle completion"
    >
      <View style={styles.dayContent}>
        <Text style={[styles.dateText, item.isToday && styles.dateTodayText]}>
          {item.dateDisplay}
          {item.isToday && ' (Today)'}
        </Text>
        <Text style={styles.goalText}>{goal.name}</Text>
      </View>
      <View style={[styles.checkbox, item.isCompleted && styles.checkboxCompleted]}>
        {item.isCompleted && <Text style={styles.checkmark}>✓</Text>}
      </View>
    </TouchableOpacity>
  );

  const getItemLayout = (_: any, index: number) => ({
    length: 76,
    offset: 76 * index,
    index,
  });

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Reconcile Days</Text>
        <Text style={styles.headerSubtitle}>
          Tap any day to toggle • {incompleteDays} incomplete
        </Text>
      </View>

      <View style={styles.daysToggle}>
        <TouchableOpacity
          style={[styles.toggleButton, days === 14 && styles.toggleButtonActive]}
          onPress={() => setDays(14)}
        >
          <Text style={[styles.toggleText, days === 14 && styles.toggleTextActive]}>
            14 days
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.toggleButton, days === 30 && styles.toggleButtonActive]}
          onPress={() => setDays(30)}
        >
          <Text style={[styles.toggleText, days === 30 && styles.toggleTextActive]}>
            30 days
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.toggleButton, days === 90 && styles.toggleButtonActive]}
          onPress={() => setDays(90)}
        >
          <Text style={[styles.toggleText, days === 90 && styles.toggleTextActive]}>
            90 days
          </Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={dayItems}
        renderItem={renderDayRow}
        keyExtractor={item => item.date}
        getItemLayout={getItemLayout}
        initialNumToRender={20}
        maxToRenderPerBatch={20}
        windowSize={10}
        style={styles.list}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 16,
    color: '#666',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  daysToggle: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    justifyContent: 'center',
  },
  toggleButton: {
    flex: 1,
    maxWidth: 100,
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    marginHorizontal: 4,
  },
  toggleButtonActive: {
    backgroundColor: '#007AFF',
  },
  toggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
  },
  toggleTextActive: {
    color: '#fff',
  },
  list: {
    flex: 1,
  },
  dayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 4,
    borderRadius: 8,
    minHeight: 76,
  },
  dayRowCompleted: {
    backgroundColor: '#E8F5E9',
  },
  dayRowToday: {
    borderWidth: 2,
    borderColor: '#007AFF',
  },
  dayContent: {
    flex: 1,
  },
  dateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  dateTodayText: {
    color: '#007AFF',
  },
  goalText: {
    fontSize: 14,
    color: '#666',
  },
  checkbox: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#ddd',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  checkboxCompleted: {
    backgroundColor: '#34C759',
    borderColor: '#34C759',
  },
  checkmark: {
    fontSize: 18,
    color: '#fff',
    fontWeight: '700',
  },
});
